import { isHexColor, type PublicSurvey } from '@survey/core';
import { Ban, CalendarClock, CheckCircle2, LockKeyhole, MailX, SearchX } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, getTokenSubject } from '../api/client';
import { useAuth } from '../auth';
import { BrandLogo } from '../components/Layout';
import { Empty, PageLoader } from '../components/ui';
import { formatDate } from '../format';
import { SurveyRunner } from '../components/SurveyRunner';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useI18n } from '../i18n';
import { deleteQueuedResponse, listQueuedResponses, markQueuePending, queueResponse, type OfflineResponse } from '../offline';
import { syncQueuedResponses } from '../offlineSync';

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; survey: PublicSurvey }
  | { kind: 'auth'; title?: string }
  | { kind: 'closed' }
  | { kind: 'not_open'; opensAt?: string }
  | { kind: 'invite'; reason: 'required' | 'invalid' | 'used' }
  | { kind: 'missing' }
  | { kind: 'error'; message: string };

/** Respondent-facing page for a published questionnaire (FR-07, FR-08). */
export function PublicSurveyPage() {
  const { code = '' } = useParams();
  // Personal invitation token (?invite=…) from an emailed link.
  const invite = new URLSearchParams(window.location.search).get('invite') || undefined;
  const { user, loading: authLoading } = useAuth();
  const { t } = useI18n();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [online, setOnline] = useState(navigator.onLine);
  const [queued, setQueued] = useState<OfflineResponse[]>([]);
  const [queuedLocally, setQueuedLocally] = useState(false);
  const [captchaSiteKey, setCaptchaSiteKey] = useState<string>();

  const refreshQueue = useCallback(async () => {
    const items = await listQueuedResponses().catch(() => []);
    const currentSurveyItems = items.filter((item) => item.surveyCode === code.toUpperCase());
    setQueued(currentSurveyItems);
    setQueuedLocally(currentSurveyItems.length > 0);
  }, [code]);

  const syncQueue = useCallback(async () => {
    if (!navigator.onLine) return;
    await syncQueuedResponses();
    await refreshQueue();
  }, [refreshQueue]);

  useEffect(() => {
    if (authLoading) return;
    let cancelled = false;
    api.publicSurvey(code, invite).then(
      (survey) => !cancelled && setState({ kind: 'ready', survey }),
      (e) => {
        if (cancelled) return;
        if (e instanceof ApiError && e.code === 'auth_required')
          setState({ kind: 'auth', title: (e.details as { title?: string } | undefined)?.title });
        else if (e instanceof ApiError && e.status === 410) setState({ kind: 'closed' });
        else if (e instanceof ApiError && e.code === 'survey_not_open')
          setState({ kind: 'not_open', opensAt: (e.details as { opensAt?: string } | undefined)?.opensAt });
        else if (e instanceof ApiError && e.code === 'invite_required') setState({ kind: 'invite', reason: 'required' });
        else if (e instanceof ApiError && e.code === 'invite_invalid') setState({ kind: 'invite', reason: 'invalid' });
        else if (e instanceof ApiError && e.code === 'invite_used') setState({ kind: 'invite', reason: 'used' });
        else if (e instanceof ApiError && e.status === 404) setState({ kind: 'missing' });
        else setState({ kind: 'error', message: e instanceof Error ? e.message : t('public.load_failed') });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [code, invite, user, authLoading]);

  useEffect(() => {
    api.captchaSiteKey().then(setCaptchaSiteKey, () => {});
  }, []);

  useEffect(() => {
    const updateOnline = () => {
      setOnline(navigator.onLine);
      if (navigator.onLine) void syncQueue();
    };
    window.addEventListener('online', updateOnline);
    window.addEventListener('offline', updateOnline);
    window.addEventListener('yellowmetrics-outbox-change', refreshQueue);
    void refreshQueue();
    void syncQueue();
    return () => {
      window.removeEventListener('online', updateOnline);
      window.removeEventListener('offline', updateOnline);
      window.removeEventListener('yellowmetrics-outbox-change', refreshQueue);
    };
  }, [refreshQueue, syncQueue]);

  return (
    <div className="public-page" style={state.kind === 'ready' && isHexColor(state.survey.definition.theme?.backgroundColor) ? { background: state.survey.definition.theme!.backgroundColor } : undefined}>
      <div className="public-head">
        <BrandLogo height={22} />
        <span className="grow" />
        {user && <span className="small muted">{t('public.signed_in_as', { email: user.email })}</span>}
        <LanguageSwitcher compact />
      </div>
      {(!online || queuedLocally || queued.length > 0) && (
        <div className={`banner ${queued.some((item) => item.status === 'failed') ? 'error' : ''}`} role="status" style={{ margin: '12px auto', maxWidth: 820 }}>
          {!online
            ? t('public.offline_mode')
            : queued.some((item) => item.status === 'failed')
              ? t('public.offline_failed')
              : queuedLocally || queued.length > 0
                ? t('public.offline_queued', { count: queued.length || 1 })
                : null}
          {online && queued.length > 0 && (
            <>
              <button className="btn sm" style={{ marginLeft: 12 }} onClick={() => void Promise.all(queued.filter((item) => item.status === 'failed').map((item) => markQueuePending(item.id))).then(syncQueue)}>{t(queued.some((item) => item.status === 'failed') ? 'public.retry_failed' : 'public.sync_now')}</button>
              {queued.some((item) => item.status === 'failed') && <button className="btn sm" style={{ marginLeft: 8 }} onClick={() => void Promise.all(queued.filter((item) => item.status === 'failed').map((item) => deleteQueuedResponse(item.id))).then(refreshQueue)}>{t('public.discard_failed')}</button>}
            </>
          )}
        </div>
      )}
      {state.kind === 'loading' && <PageLoader />}
      {state.kind === 'ready' && (state.survey.invitee || state.survey.closesAt) && (
        <div className="banner" role="status" style={{ margin: '12px auto', maxWidth: 820 }}>
          {[
            state.survey.invitee ? t('public.invite_hello', { name: state.survey.invitee }) : '',
            state.survey.closesAt ? t('public.closes_on', { date: formatDate(state.survey.closesAt, true) }) : '',
          ]
            .filter(Boolean)
            .join(' ')}
        </div>
      )}
      {state.kind === 'ready' && (
        <SurveyRunner
          definition={state.survey.definition}
          survey={{ code: state.survey.code, number: state.survey.number, version: state.survey.version, title: state.survey.title, description: state.survey.description }}
          storageKey={`survey.session.${state.survey.code}.v${state.survey.version}`}
          captchaSiteKey={online ? captchaSiteKey : undefined}
          fullQuotas={state.survey.fullQuotas}
          flowCounts={state.survey.flowCounts}
          claimPicks={online ? async (submissionId) => (await api.claimFlowPicks(state.survey.code, submissionId)).picks : undefined}
          embedded={Object.fromEntries(new URLSearchParams(window.location.search))}
          uploader={(file) => api.uploadRespondentFile(state.survey.code, file)}
          onSubmit={async (answered, captchaToken) => {
            const submission = invite ? { ...answered, inviteToken: invite } : answered;
            if (!navigator.onLine) {
              await queueResponse(state.survey.code, submission, getTokenSubject());
              setQueuedLocally(true);
              await refreshQueue();
              return;
            }
            try {
              await api.submit(state.survey.code, submission, captchaToken);
            } catch (e) {
              if (e instanceof ApiError && (e.status === 0 || e.status >= 500)) {
                await queueResponse(state.survey.code, submission, getTokenSubject());
                setQueuedLocally(true);
                await refreshQueue();
                return;
              }
              if (e instanceof ApiError && e.status === 422 && Object.keys(e.fields).length) {
                throw new Error(`${e.message} ${Object.values(e.fields)[0]}`);
              }
              throw e;
            }
          }}
        />
      )}
      {state.kind === 'auth' && (
        <div className="card runner">
          <Empty icon={<LockKeyhole />} title={state.title ? t('public.requires_signin_title', { title: state.title }) : t('public.signin_required')}>
            <p className="small">{t('public.signin_body')}</p>
            <div className="btn-row mt-8">
              <Link className="btn primary" to={`/login?next=${encodeURIComponent(`/s/${code}`)}`}>
                {t('auth.sign_in')}
              </Link>
              <Link className="btn" to={`/register?next=${encodeURIComponent(`/s/${code}`)}`}>
                {t('public.create_account')}
              </Link>
            </div>
          </Empty>
        </div>
      )}
      {state.kind === 'closed' && (
        <div className="card runner">
          <Empty icon={<Ban />} title={t('public.closed_title')}>
            <p className="small">{t('public.closed_body')}</p>
          </Empty>
        </div>
      )}
      {state.kind === 'not_open' && (
        <div className="card runner">
          <Empty icon={<CalendarClock />} title={t('public.not_open_title')}>
            <p className="small">{state.opensAt ? t('public.not_open_body', { date: formatDate(state.opensAt, true) }) : t('public.not_open_soon')}</p>
          </Empty>
        </div>
      )}
      {state.kind === 'invite' && (
        <div className="card runner">
          <Empty icon={state.reason === 'used' ? <CheckCircle2 /> : <MailX />} title={t(`public.invite_${state.reason}_title`)}>
            <p className="small">{t(`public.invite_${state.reason}_body`)}</p>
          </Empty>
        </div>
      )}
      {state.kind === 'missing' && (
        <div className="card runner">
          <Empty icon={<SearchX />} title={t('public.missing_title')}>
            <p className="small">{t('public.missing_body', { code })}</p>
          </Empty>
        </div>
      )}
      {state.kind === 'error' && (
        <div className="runner">
          <div className="banner error">{state.message}</div>
        </div>
      )}
    </div>
  );
}
